"use client";

import { useTranslations } from "@i18n/react";
import { useEffect, useState } from "react";

import { consoleErrorPackagesSdk } from "../utils/consoleErrorPackagesSdk";
import { cropImageToBlob } from "../utils/image-uploads/cropImageToBlob";
import { describeImageUploadFailure } from "../utils/image-uploads/imageUploadError.error";
import { SUPPORTED_IMAGE_TYPES_LABEL } from "../utils/image-uploads/imageUploadLimits.constants";
import type {
  ImageUploadFailureReason,
  PresignedResult,
  SelectedImage,
} from "./useImageUpload";
import { useImageUpload } from "./useImageUpload";

type Point = { x: number; y: number };
type Area = { x: number; y: number; width: number; height: number };

type CropState = {
  imageSrc: string;
  crop: Point;
  zoom: number;
  pixelCrop: Area | null;
};

type CroppedImage = {
  blob: Blob;
  previewUrl: string;
  originalSrc: string;
  lastCropState: { crop: Point; zoom: number };
};

export type { SelectedImage, PresignedResult };

/** Crop runs entirely in the browser, so it can't fail for any upload reason. */
export type CroppedImageUploadFailureReason = ImageUploadFailureReason | "crop";

export const useCroppedImageUpload = ({
  getPresignedUrl,
  onUploadComplete,
}: {
  getPresignedUrl: () => Promise<PresignedResult>;
  onUploadComplete: (image: SelectedImage) => void;
}) => {
  const t = useTranslations();
  const [cropState, setCropState] = useState<CropState | null>(null);
  const [croppedImage, setCroppedImage] = useState<CroppedImage | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);

  const {
    upload,
    isUploading,
    error: uploadError,
    errorReason: uploadErrorReason,
    clearError: clearUploadError,
  } = useImageUpload({ getPresignedUrl, onUploadComplete });

  useEffect(() => {
    return () => {
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage.previewUrl);
        if (croppedImage.originalSrc.startsWith("blob:")) {
          URL.revokeObjectURL(croppedImage.originalSrc);
        }
      }
    };
  }, [croppedImage]);

  const startCrop = (
    src: string,
    lastCropState?: { crop: Point; zoom: number },
  ) => {
    setCropState({
      imageSrc: src,
      crop: lastCropState?.crop ?? { x: 0, y: 0 },
      zoom: lastCropState?.zoom ?? 1,
      pixelCrop: null,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) startCrop(URL.createObjectURL(file));
  };

  const confirmCrop = async () => {
    if (!cropState?.pixelCrop) return;
    setIsCropping(true);
    setCropError(null);
    try {
      const blob = await cropImageToBlob(
        cropState.imageSrc,
        cropState.pixelCrop,
      );
      setCroppedImage({
        blob,
        previewUrl: URL.createObjectURL(blob),
        originalSrc: cropState.imageSrc,
        lastCropState: { crop: cropState.crop, zoom: cropState.zoom },
      });
      setCropState(null);
    } catch (e) {
      consoleErrorPackagesSdk({
        error: describeImageUploadFailure(e),
        heading: "Image crop failed",
      });
      // The decode failures that land here — CMYK JPEGs, a HEIC renamed .jpeg —
      // are the ones where naming the formats we handle actually helps.
      setCropError(
        t("events_image_crop_error", { types: SUPPORTED_IMAGE_TYPES_LABEL }),
      );
    } finally {
      setIsCropping(false);
    }
  };

  const save = async (): Promise<SelectedImage | null> => {
    if (!croppedImage) return null;
    const image = await upload(croppedImage.blob, {
      originalSrc: croppedImage.originalSrc,
      lastCropState: croppedImage.lastCropState,
    });
    if (image) setCroppedImage(null);
    return image;
  };

  return {
    cropState,
    setCropState,
    croppedImage,
    clearCroppedImage: () => setCroppedImage(null),
    isUploading: isCropping || isUploading,
    error: cropError ?? uploadError,
    errorReason: (cropError
      ? "crop"
      : uploadErrorReason) as CroppedImageUploadFailureReason | null,
    clearError: () => {
      setCropError(null);
      clearUploadError();
    },
    handleFileChange,
    confirmCrop,
    startCrop,
    save,
  };
};
